import axios from 'axios';
import React, { useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../utils/getData/User';
import AuthContext from '../../utils/auth/AuthContext';
import { preloadEffectVideo, PRELOAD_VIDEO_NAME } from '../../utils/media/videoPreload';
const apiUrl = process.env.REACT_APP_BACKEND_API_URL;

// 購入APIの応答待ちの上限(ミリ秒)。これが無いと応答が返らない場合に
// ローディング画面から永久に抜けられなくなる。
const PURCHASE_TIMEOUT_MS = 30000;
// チケット残数の再取得の上限。購入結果の表示を妨げないよう短くする。
const USER_FETCH_TIMEOUT_MS = 10000;

// 購入が成立したか判断できない場合(タイムアウト・通信断・5xx)と、
// 成立しなかったことが確実な場合(4xx)で案内を分ける。
// サーバ側は在庫とチケットを減らしてからレスポンスを返すため、前者では
// 既にチケットが消費されている可能性がある。安易に再試行させてはいけない。
type PurchaseError = {
  title: string;
  message: string;
  showAlbumLink: boolean;
};

const UNCERTAIN_ERROR: PurchaseError = {
  title: '通信に時間がかかっています',
  message: 'カードが獲得できている可能性があります。'
    + 'お手数ですが、アルバムとチケット残数をご確認ください。'
    + '獲得できていない場合は、時間をおいて再度お試しください。',
  showAlbumLink: true,
};

// サーバが返すエラー文言と利用者向けの案内の対応
const FAILED_MESSAGES: Record<string, string> = {
  'Insufficient tickets': 'チケットが不足しています。',
  'This product is not on sale.': 'この商品は現在販売していません。',
  'This product contains a rarity with no stock.': '在庫切れのため販売を終了しました。',
  'Product not found': '商品が見つかりませんでした。',
};

const buildFailedError = (serverMessage?: string): PurchaseError => ({
  title: '購入できませんでした',
  message: (serverMessage && FAILED_MESSAGES[serverMessage])
    || '購入処理を完了できませんでした。時間をおいて再度お試しください。',
  // 購入が成立していないことが確実なので、アルバムを確認する必要はない
  showAlbumLink: false,
});

type PurchaseButtonProps = {
  teamColor: string;
  product_code: string;
  setProductValiationId: React.Dispatch<React.SetStateAction<string>>;
  setRarityName: React.Dispatch<React.SetStateAction<string>>;
  setRarityGroup: React.Dispatch<React.SetStateAction<string>>;
  setProductValiationName: React.Dispatch<React.SetStateAction<string>>;
  setRareEffect: React.Dispatch<React.SetStateAction<string>>;
  setVideoName: React.Dispatch<React.SetStateAction<string>>;
  setTickets: React.Dispatch<React.SetStateAction<number>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  // 購入が成功したときだけ呼ばれる。失敗時は呼ばれない。
  onPurchased?: () => void;
};

interface PurchaseErrorModalProps {
  error: PurchaseError | null;
  onHide: () => void;
}

function PurchaseErrorModal({ error, onHide }: PurchaseErrorModalProps) {
  return (
    <Modal show={Boolean(error)} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{error?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="m-2">{error?.message}</p>
        {error?.showAlbumLink && (
          <Link className="btn btn-secondary w-250 mx-auto my-2 py-1 d-block" to="/album">
            アルバムを確認する
          </Link>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>閉じる</Button>
      </Modal.Footer>
    </Modal>
  );
}

interface PlayModalProps {
  show: boolean;
  onHide: () => void;
  purchase: () => Promise<void>;
}

function PlayModal({ show, onHide, purchase }: PlayModalProps) {
  return (
    <Modal
      show={show}
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          商品の購入について
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-center align-items-center my-2">
          <img className="w-25" alt="ticket" src="/image/ticket.png" />
          <p className="fs-topic fw-bold">× 1</p>
        </div>
        商品の購入にはチケットを1枚使用します。
        <Button variant="primary" onClick={purchase} className="btn btn-secondary w-250 mx-auto my-2 py-1">
          商品購入
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>閉じる</Button>
      </Modal.Footer>
    </Modal>
  );
}

const PurchaseButton: React.FC<PurchaseButtonProps> = ({
  teamColor,
  product_code,
  setProductValiationId,
  setProductValiationName,
  setRarityName,
  setRarityGroup,
  setRareEffect,
  setVideoName,
  setTickets,
  setLoading,
  setIsClicked,
  onPurchased,
}) => {
  const [modalShow, setModalShow] = React.useState(false);
  const [purchaseError, setPurchaseError] = React.useState<PurchaseError | null>(null);
  const { setAuthErrorFlag } = useContext(AuthContext);

  // 認証エラー以外でログアウトさせないためにエラーを分類する
  const handlePurchaseError = (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.response) {
      // タイムアウト・通信断。購入が成立したかどうか分からない
      setPurchaseError(UNCERTAIN_ERROR);
      return;
    }

    const status = error.response.status;
    if (status === 401) {
      // セッション切れのときだけログアウトさせる
      setAuthErrorFlag(true);
      return;
    }

    if (status >= 500) {
      // サーバ側で処理済みの可能性が残るため、成立不明として扱う
      setPurchaseError(UNCERTAIN_ERROR);
      return;
    }

    // 4xx はDBを更新する前に返しているので、購入は成立していない
    setPurchaseError(buildFailedError(error.response.data?.error));
  };

  const purchase = async () => {
    try {
      setLoading(true);
      const postData = { product_code: product_code };
      const response = await axios.post(`${apiUrl}/purchaseProduct/`, postData, {
        withCredentials: true,
        timeout: PURCHASE_TIMEOUT_MS,
      });
      setProductValiationId(response.data.product.id);
      setProductValiationName(response.data.product.name);
      setRarityName(response.data.product.rarity_name);
      setRarityGroup(response.data.product.rarity_group);
      setRareEffect(response.data.product.effect);
      setVideoName(response.data.product.video);
      // 同じカードを連続で引いた場合でも演出を再生させる
      if (onPurchased) {
        onPurchased();
      }

      // チケット残数の更新に失敗しても購入結果の表示は妨げない。
      // ここで例外を外に出すと、購入が成功しているのに失敗扱いになってしまう。
      try {
        const user = await getCurrentUser({ timeout: USER_FETCH_TIMEOUT_MS });
        setTickets(user.tickets);
      } catch {
        // 残数は次の画面遷移で更新される
      }
    } catch (error) {
      handlePurchaseError(error);
    } finally {
      if (setIsClicked) {
        setIsClicked(false);
      };
      setLoading(false);
      setModalShow(false);
    }
  };

  return (
    <>
      <Button className={`bg-${teamColor}-primary btn w-75 mx-auto m-2`} variant="primary" onClick={() => {
        // 購入確認モーダルを開いた時点で演出動画の先読みを始める
        preloadEffectVideo(PRELOAD_VIDEO_NAME);
        setModalShow(true);
      }}>
        商品購入
      </Button>

      <PlayModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        purchase={purchase}
      />

      <PurchaseErrorModal
        error={purchaseError}
        onHide={() => setPurchaseError(null)}
      />
    </>
  );
};

export default PurchaseButton;
